import { useState, useEffect } from 'react';
import { v2Layouts, v2Starters, type V2Layout, type V2Starter } from '../data/templates';

import '../styles/v2-picker.css';

interface V2LayoutPickerProps {
  onSelect: (templateId: string, designVariables: Record<string, unknown>) => void;
  /** Currently selected starter id (for showing selection state) */
  selectedStarterId?: string;
}

/* ─── Layout wireframe SVGs ──────────────────────────────────────── */

function LayoutIcon({ layoutId }: { layoutId: string }) {
  const w = 120;
  const h = 80;
  const stroke = '#94a3b8';
  const fill = '#e2e8f0';
  const accent = 'var(--color-primary, #2d6a7e)';

  switch (layoutId) {
    case 'standard':
      return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Top nav bar */}
          <rect x="0" y="0" width={w} height="12" rx="2" fill={accent} opacity="0.8" />
          <rect x="4" y="3" width="16" height="6" rx="1" fill="#fff" opacity="0.7" />
          <rect x="80" y="4" width="8" height="4" rx="1" fill="#fff" opacity="0.4" />
          <rect x="92" y="4" width="8" height="4" rx="1" fill="#fff" opacity="0.4" />
          <rect x="104" y="4" width="8" height="4" rx="1" fill="#fff" opacity="0.4" />
          {/* Full-width hero */}
          <rect x="0" y="14" width={w} height="30" rx="2" fill={fill} />
          <rect x="35" y="22" width="50" height="5" rx="1" fill={stroke} opacity="0.5" />
          <rect x="42" y="30" width="36" height="3" rx="1" fill={stroke} opacity="0.3" />
          <rect x="48" y="36" width="24" height="5" rx="2" fill={accent} opacity="0.6" />
          {/* Content blocks */}
          <rect x="4" y="48" width="35" height="28" rx="2" fill={fill} />
          <rect x="43" y="48" width="35" height="28" rx="2" fill={fill} />
          <rect x="82" y="48" width="35" height="28" rx="2" fill={fill} />
        </svg>
      );
    case 'split':
      return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Top nav bar */}
          <rect x="0" y="0" width={w} height="12" rx="2" fill={accent} opacity="0.8" />
          <rect x="4" y="3" width="16" height="6" rx="1" fill="#fff" opacity="0.7" />
          <rect x="80" y="4" width="8" height="4" rx="1" fill="#fff" opacity="0.4" />
          <rect x="92" y="4" width="8" height="4" rx="1" fill="#fff" opacity="0.4" />
          <rect x="104" y="4" width="8" height="4" rx="1" fill="#fff" opacity="0.4" />
          {/* Split hero — text left, image right */}
          <rect x="0" y="14" width="58" height="36" rx="2" fill={fill} />
          <rect x="6" y="20" width="30" height="5" rx="1" fill={stroke} opacity="0.5" />
          <rect x="6" y="28" width="44" height="3" rx="1" fill={stroke} opacity="0.3" />
          <rect x="6" y="34" width="20" height="5" rx="2" fill={accent} opacity="0.6" />
          <rect x="62" y="14" width="58" height="36" rx="2" fill={stroke} opacity="0.2" />
          {/* Content blocks */}
          <rect x="4" y="54" width="54" height="22" rx="2" fill={fill} />
          <rect x="62" y="54" width="54" height="22" rx="2" fill={fill} />
        </svg>
      );
    case 'sidebar':
      return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Sidebar */}
          <rect x="0" y="0" width="28" height={h} rx="2" fill={accent} opacity="0.8" />
          <rect x="4" y="6" width="20" height="5" rx="1" fill="#fff" opacity="0.7" />
          <rect x="4" y="16" width="16" height="3" rx="1" fill="#fff" opacity="0.35" />
          <rect x="4" y="22" width="16" height="3" rx="1" fill="#fff" opacity="0.35" />
          <rect x="4" y="28" width="16" height="3" rx="1" fill="#fff" opacity="0.35" />
          <rect x="4" y="34" width="16" height="3" rx="1" fill="#fff" opacity="0.35" />
          {/* Main content */}
          <rect x="32" y="4" width="84" height="24" rx="2" fill={fill} />
          <rect x="44" y="12" width="60" height="5" rx="1" fill={stroke} opacity="0.4" />
          {/* Grid */}
          <rect x="32" y="32" width="40" height="20" rx="2" fill={fill} />
          <rect x="76" y="32" width="40" height="20" rx="2" fill={fill} />
          <rect x="32" y="56" width="40" height="20" rx="2" fill={fill} />
          <rect x="76" y="56" width="40" height="20" rx="2" fill={fill} />
        </svg>
      );
    case 'landing':
      return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Transparent nav overlay */}
          <rect x="0" y="0" width={w} height="10" rx="2" fill={stroke} opacity="0.15" />
          <rect x="4" y="2" width="16" height="6" rx="1" fill={stroke} opacity="0.3" />
          <rect x="80" y="3" width="8" height="4" rx="1" fill={stroke} opacity="0.3" />
          <rect x="92" y="3" width="8" height="4" rx="1" fill={stroke} opacity="0.3" />
          <rect x="104" y="3" width="8" height="4" rx="1" fill={stroke} opacity="0.3" />
          {/* Full-page hero */}
          <rect x="0" y="0" width={w} height="38" rx="2" fill={accent} opacity="0.15" />
          <rect x="25" y="14" width="70" height="6" rx="1" fill={accent} opacity="0.5" />
          <rect x="35" y="24" width="50" height="3" rx="1" fill={stroke} opacity="0.3" />
          <rect x="44" y="30" width="32" height="5" rx="2" fill={accent} opacity="0.6" />
          {/* Sections stacked */}
          <rect x="8" y="42" width="104" height="14" rx="2" fill={fill} />
          <rect x="8" y="60" width="104" height="14" rx="2" fill={fill} opacity="0.7" />
        </svg>
      );
    default:
      return null;
  }
}

/* ─── Starter Card ──────────────────────────────────────────────── */

function StarterCard({
  starter,
  isSelected,
  onSelect,
}: {
  starter: V2Starter;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const v = starter.variables;
  const colorBg = (v.colorBackground as string) || '#ffffff';
  const colorPrimary = (v.colorPrimary as string) || '#1a1a1a';
  const colorSecondary = (v.colorSecondary as string) || '#666666';
  const colorAccent = (v.colorAccent as string) || colorSecondary;
  const colorText = (v.colorText as string) || '#333333';
  const colorHeading = (v.colorHeading as string) || colorPrimary;
  const fontHeading = (v.fontHeading as string) || 'Inter';
  const fontBody = (v.fontBody as string) || 'Inter';

  return (
    <label
      className={`tp-card v2-starter-card${isSelected ? ' tp-card-selected' : ''}`}
      style={{
        '--tp-primary': colorPrimary,
        '--tp-secondary': colorSecondary,
        '--tp-bg': colorBg,
        '--tp-text': colorText,
      } as React.CSSProperties}
    >
      <input
        type="radio"
        name="v2-starter"
        value={starter.id}
        checked={isSelected}
        onChange={onSelect}
        className="tp-radio"
      />

      {/* Mini preview */}
      <div className="tp-preview" style={{ background: colorBg }}>
        <div className="tp-preview-header" style={{ background: colorPrimary }}>
          <div className="tp-preview-logo" style={{ background: colorBg, opacity: 0.9 }} />
          <div className="tp-preview-nav">
            <div className="tp-preview-nav-item" style={{ background: colorBg, opacity: 0.5 }} />
            <div className="tp-preview-nav-item" style={{ background: colorBg, opacity: 0.5 }} />
            <div className="tp-preview-nav-item" style={{ background: colorBg, opacity: 0.5 }} />
          </div>
        </div>
        <div className="tp-preview-hero">
          <div
            className="tp-preview-hero-bg"
            style={{
              background: `linear-gradient(135deg, ${colorPrimary}22, ${colorSecondary}33)`,
            }}
          />
          <div
            className="tp-preview-heading-text"
            style={{ fontFamily: `'${fontHeading}', serif`, color: colorHeading }}
          >
            Your Brand
          </div>
          <div
            className="tp-preview-body-text"
            style={{ fontFamily: `'${fontBody}', sans-serif`, color: colorText }}
          >
            Beautiful websites for creators
          </div>
          <div className="tp-preview-btn" style={{ background: colorAccent || colorPrimary }} />
        </div>
      </div>

      {/* Card info */}
      <div className="tp-info">
        <div className="tp-info-header">
          <h3 className="tp-name">{starter.name}</h3>
          {isSelected && (
            <span className="tp-check" aria-label="Selected">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="var(--color-primary)" />
                <path d="M6 10l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
        </div>
        <p className="tp-description">{starter.description}</p>
        <div className="tp-swatches">
          <span className="tp-swatch" style={{ background: colorPrimary }} title="Primary" />
          <span className="tp-swatch" style={{ background: colorSecondary }} title="Secondary" />
          <span className="tp-swatch" style={{ background: colorAccent }} title="Accent" />
          <span className="tp-swatch" style={{ background: colorBg, border: '1px solid #d1d5db' }} title="Background" />
          <span className="tp-swatch" style={{ background: colorText }} title="Text" />
        </div>
        <div className="tp-fonts">
          {fontHeading} · {fontBody}
        </div>
      </div>
    </label>
  );
}

/* ─── Main Picker ──────────────────────────────────────────────── */

export default function V2LayoutPicker({ onSelect, selectedStarterId }: V2LayoutPickerProps) {
  const [selectedLayout, setSelectedLayout] = useState<V2Layout | null>(null);
  const [selectedStarter, setSelectedStarter] = useState<string>(selectedStarterId || '');

  // Load Google Fonts for starter previews
  useEffect(() => {
    const fonts = new Set<string>();
    v2Starters.forEach((s) => {
      if (s.variables.fontHeading) fonts.add(s.variables.fontHeading as string);
      if (s.variables.fontBody) fonts.add(s.variables.fontBody as string);
    });
    const families = Array.from(fonts)
      .map((f) => encodeURIComponent(f).replace(/%20/g, '+') + ':wght@400;600;700')
      .join('&family=');
    if (families) {
      const linkId = 'v2-picker-fonts';
      if (!document.getElementById(linkId)) {
        const link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
        document.head.appendChild(link);
      }
    }
  }, []);

  const handleLayoutSelect = (layout: V2Layout) => {
    setSelectedLayout(layout);
    setSelectedStarter('');
  };

  const handleStarterSelect = (starter: V2Starter) => {
    setSelectedStarter(starter.id);
    const layout = v2Layouts.find((l) => l.id === starter.layout);
    if (layout) {
      onSelect(layout.templateId, { ...starter.variables });
    }
  };

  const handleBack = () => {
    setSelectedLayout(null);
    setSelectedStarter('');
  };

  const filteredStarters = selectedLayout
    ? v2Starters.filter((s) => s.layout === selectedLayout.id)
    : [];

  // Step 1: Choose Layout
  if (!selectedLayout) {
    return (
      <div className="v2-picker">
        <div className="v2-picker-step-header">
          <span className="v2-picker-step-label">Step 1 of 2</span>
          <h3 className="v2-picker-step-title">Choose a layout</h3>
          <p className="v2-picker-step-desc">
            This determines how your site is structured. You can pick colours and fonts next.
          </p>
        </div>
        <div className="v2-layout-grid">
          {v2Layouts.map((layout) => {
            const starterCount = v2Starters.filter((s) => s.layout === layout.id).length;
            return (
              <button
                key={layout.id}
                type="button"
                className="v2-layout-card"
                onClick={() => handleLayoutSelect(layout)}
              >
                <div className="v2-layout-card-icon">
                  <LayoutIcon layoutId={layout.id} />
                </div>
                <div className="v2-layout-card-info">
                  <h4 className="v2-layout-card-name">{layout.name}</h4>
                  <p className="v2-layout-card-desc">{layout.description}</p>
                  <span className="v2-layout-card-count">
                    {starterCount} starting {starterCount === 1 ? 'point' : 'points'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Step 2: Choose Starter
  return (
    <div className="v2-picker">
      <div className="v2-picker-step-header">
        <button type="button" className="v2-picker-back" onClick={handleBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to layouts
        </button>
        <span className="v2-picker-step-label">Step 2 of 2</span>
        <h3 className="v2-picker-step-title">
          Pick a starting point
          <span className="v2-picker-layout-badge">{selectedLayout.name}</span>
        </h3>
        <p className="v2-picker-step-desc">
          Choose a look to start with. You can customise everything later.
        </p>
      </div>
      <div className="tp-grid">
        {filteredStarters.map((starter) => (
          <StarterCard
            key={starter.id}
            starter={starter}
            isSelected={selectedStarter === starter.id}
            onSelect={() => handleStarterSelect(starter)}
          />
        ))}
      </div>
    </div>
  );
}
